import React, { useState, useEffect } from 'react';
import { useAuth } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { 
  Settings2, Plus, Edit2, Trash2, TrendingUp, TrendingDown, 
  Equal, Activity, Shield, FileText, Heart, Briefcase, Users,
  Target, Hash, Percent, Type
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  { value: 'clinical', label: 'Clinical Quality', icon: Activity, color: 'text-emerald-500' },
  { value: 'safety', label: 'Safety & Incidents', icon: Shield, color: 'text-orange-500' },
  { value: 'regulatory', label: 'Regulatory Compliance', icon: FileText, color: 'text-purple-500' },
  { value: 'patient_experience', label: 'Patient Experience', icon: Heart, color: 'text-red-500' },
  { value: 'staff', label: 'Staff Health', icon: Briefcase, color: 'text-blue-500' },
  { value: 'program', label: 'Program Overview', icon: Users, color: 'text-cyan-500' }
];

const DATA_TYPES = [
  { value: 'number', label: 'Number', icon: Hash },
  { value: 'percent', label: 'Percentage', icon: Percent },
  { value: 'count', label: 'Count', icon: TrendingUp },
  { value: 'text', label: 'Text', icon: Type }
];

const DIRECTION_OPTIONS = [
  { value: 'higher', label: 'Higher is Better', icon: TrendingUp },
  { value: 'lower', label: 'Lower is Better', icon: TrendingDown },
  { value: 'equal', label: 'Target Exact', icon: Equal }
];

const MetricCard = ({ metric, onEdit, onDelete }) => {
  const category = CATEGORIES.find(c => c.value === metric.category);
  const dataType = DATA_TYPES.find(d => d.value === metric.data_type);
  const direction = DIRECTION_OPTIONS.find(d => d.value === metric.target_direction);
  const CategoryIcon = category?.icon || Activity;
  const DirectionIcon = direction?.icon || TrendingUp;

  return (
    <Card className="stat-card group" data-testid={`metric-card-${metric.id}`}>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${category?.color === 'text-emerald-500' ? 'from-emerald-500/20 to-green-500/10' : 
              category?.color === 'text-orange-500' ? 'from-orange-500/20 to-yellow-500/10' :
              category?.color === 'text-purple-500' ? 'from-purple-500/20 to-indigo-500/10' :
              category?.color === 'text-red-500' ? 'from-red-500/20 to-pink-500/10' :
              category?.color === 'text-blue-500' ? 'from-blue-500/20 to-indigo-500/10' :
              'from-cyan-500/20 to-teal-500/10'
            } flex items-center justify-center`}>
              <CategoryIcon className={`h-5 w-5 ${category?.color}`} />
            </div>
            <Badge variant="outline">{category?.label}</Badge>
          </div>
          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onEdit(metric)}>
              <Edit2 className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => onDelete(metric.id)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <h3 className="font-semibold text-lg mb-1">{metric.display_name}</h3>
        <p className="text-sm text-muted-foreground font-mono mb-3">{metric.name}</p>
        
        {metric.description && (
          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{metric.description}</p>
        )}

        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="flex items-center gap-2">
            <dataType.icon className="h-4 w-4 text-muted-foreground" />
            <span>{dataType?.label}</span>
            {metric.unit && <span className="text-muted-foreground">({metric.unit})</span>}
          </div>
          <div className="flex items-center gap-2">
            <DirectionIcon className="h-4 w-4 text-muted-foreground" />
            <span>{direction?.label}</span>
          </div>
        </div>

        {metric.target_value !== null && metric.target_value !== undefined && (
          <div className="mt-4 pt-4 border-t flex items-center gap-2">
            <Target className="h-4 w-4 text-primary" />
            <span className="text-sm font-medium">Target: {metric.target_value}{metric.data_type === 'percent' ? '%' : ''} {metric.unit}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const MetricDialog = ({ open, onOpenChange, onSubmit, editingMetric }) => {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    category: 'clinical',
    data_type: 'number',
    target_value: '',
    target_direction: 'higher',
    unit: '',
    description: ''
  });

  useEffect(() => {
    if (editingMetric) {
      setFormData({
        name: editingMetric.name || '',
        display_name: editingMetric.display_name || '',
        category: editingMetric.category || 'clinical',
        data_type: editingMetric.data_type || 'number',
        target_value: editingMetric.target_value?.toString() || '',
        target_direction: editingMetric.target_direction || 'higher',
        unit: editingMetric.unit || '',
        description: editingMetric.description || ''
      });
    } else {
      setFormData({
        name: '',
        display_name: '',
        category: 'clinical',
        data_type: 'number',
        target_value: '',
        target_direction: 'higher',
        unit: '',
        description: ''
      });
    }
  }, [editingMetric, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const processed = {
      ...formData,
      name: formData.name.toLowerCase().replace(/\s+/g, '_'),
      target_value: formData.target_value ? parseFloat(formData.target_value) : null
    };
    onSubmit(processed, editingMetric?.id);
  };

  const generateName = (displayName) => {
    return displayName.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingMetric ? 'Edit Custom Metric' : 'Create Custom Metric'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Display Name *</Label>
                <Input
                  value={formData.display_name}
                  onChange={(e) => {
                    setFormData({ 
                      ...formData, 
                      display_name: e.target.value,
                      name: !editingMetric ? generateName(e.target.value) : formData.name
                    });
                  }}
                  placeholder="e.g., Wound Healing Rate"
                  required
                  data-testid="metric-display-name"
                />
              </div>

              <div className="space-y-2">
                <Label>System Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: generateName(e.target.value) })}
                  placeholder="e.g., wound_healing_rate"
                  className="font-mono"
                  required
                  data-testid="metric-name"
                />
                <p className="text-xs text-muted-foreground">Used internally, lowercase with underscores</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                    <SelectTrigger data-testid="metric-category">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CATEGORIES.map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>
                          <div className="flex items-center gap-2">
                            <cat.icon className={`h-4 w-4 ${cat.color}`} />
                            {cat.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Data Type *</Label>
                  <Select value={formData.data_type} onValueChange={(v) => setFormData({ ...formData, data_type: v })}>
                    <SelectTrigger data-testid="metric-datatype">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DATA_TYPES.map(dt => (
                        <SelectItem key={dt.value} value={dt.value}>
                          <div className="flex items-center gap-2">
                            <dt.icon className="h-4 w-4" />
                            {dt.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Target Value</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={formData.target_value}
                    onChange={(e) => setFormData({ ...formData, target_value: e.target.value })}
                    placeholder="e.g., 95"
                    data-testid="metric-target"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Input
                    value={formData.unit}
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    placeholder="e.g., days, patients"
                    data-testid="metric-unit"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Target Direction</Label>
                <Select value={formData.target_direction} onValueChange={(v) => setFormData({ ...formData, target_direction: v })}>
                  <SelectTrigger data-testid="metric-direction">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DIRECTION_OPTIONS.map(dir => (
                      <SelectItem key={dir.value} value={dir.value}>
                        <div className="flex items-center gap-2">
                          <dir.icon className="h-4 w-4" />
                          {dir.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe what this metric measures and why it's important..."
                  className="min-h-[80px]"
                  data-testid="metric-description"
                />
              </div>
            </div>
          </ScrollArea>

          <DialogFooter className="mt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" data-testid="metric-submit">{editingMetric ? 'Update' : 'Create'} Metric</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function CustomMetrics() {
  const { token } = useAuth();
  const [metrics, setMetrics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingMetric, setEditingMetric] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');

  const fetchMetrics = async () => {
    try {
      const url = filterCategory === 'all' 
        ? `${API_URL}/api/custom-metrics`
        : `${API_URL}/api/custom-metrics?category=${filterCategory}`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setMetrics(data);
      }
    } catch (error) {
      toast.error('Failed to load custom metrics');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
  }, [token, filterCategory]);

  const handleSubmit = async (data, editId) => {
    try {
      const url = editId ? `${API_URL}/api/custom-metrics/${editId}` : `${API_URL}/api/custom-metrics`;
      const method = editId ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        toast.success(editId ? 'Metric updated' : 'Metric created');
        fetchMetrics();
        setDialogOpen(false);
        setEditingMetric(null);
      } else {
        const error = await response.json();
        toast.error('Failed to save', { description: error.detail });
      }
    } catch (error) {
      toast.error('Connection error');
    }
  };

  const handleDelete = async (metricId) => {
    if (!window.confirm('Delete this custom metric?')) return;

    try {
      const response = await fetch(`${API_URL}/api/custom-metrics/${metricId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        toast.success('Metric deleted');
        fetchMetrics();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="custom-metrics-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-10 w-36" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-52" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="custom-metrics-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-3">
            <Settings2 className="h-8 w-8 text-primary" />
            Custom Metrics
          </h1>
          <p className="text-muted-foreground mt-1">Define and configure your own quality metrics</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="create-metric-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Metric
        </Button>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={filterCategory === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setFilterCategory('all')}
        >
          All Categories
        </Button>
        {CATEGORIES.map(cat => (
          <Button
            key={cat.value}
            variant={filterCategory === cat.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterCategory(cat.value)}
            className="gap-2"
          >
            <cat.icon className={`h-4 w-4 ${filterCategory === cat.value ? '' : cat.color}`} />
            {cat.label}
          </Button>
        ))}
      </div>

      {/* Metrics Grid */}
      {metrics.length === 0 ? (
        <Card className="py-16">
          <CardContent className="flex flex-col items-center justify-center text-center">
            <div className="h-20 w-20 rounded-full bg-gradient-to-br from-cyan-500/20 to-teal-500/10 flex items-center justify-center mb-6">
              <Settings2 className="h-10 w-10 text-primary" />
            </div>
            <h3 className="font-semibold text-xl mb-2">No Custom Metrics</h3>
            <p className="text-muted-foreground mb-6 max-w-md">
              Create custom metrics to track quality indicators specific to your hospice organization.
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Metric
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metrics.map((metric, index) => (
            <div key={metric.id} className="animate-fade-in" style={{ animationDelay: `${index * 50}ms` }}>
              <MetricCard
                metric={metric}
                onEdit={(m) => {
                  setEditingMetric(m);
                  setDialogOpen(true);
                }}
                onDelete={handleDelete}
              />
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <MetricDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) setEditingMetric(null);
        }}
        onSubmit={handleSubmit}
        editingMetric={editingMetric}
      />
    </div>
  );
}
